import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/DropMenu.css";
import { translateText, translateLabel, useTranslationSnapshot } from "./../translations/runtime";

export default function CustomDropdown({
  multi = false,
  value,
  onChange,
  options = [],
  placeholder = "Select",
  leftIcon = null,
  rightIcon = null,
  className = "",
  openClassName = "",
  menuClassName = "",
  optionClassName = "",
  disabled = false,
  searchable = false,
  renderTrigger = null,
  renderMenu = null,
}) {
  useTranslationSnapshot();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const menuId = useRef(
    `custom-dropdown-${Math.random().toString(36).slice(2)}`,
  ).current;

  const normalized = (options || []).map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt,
  );

  const filtered = useMemo(() => {
    if (!searchable) return normalized;

    const q = search.trim().toLowerCase();
    if (!q) return normalized;

    return normalized.filter((o) =>
      String(o.label ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [normalized, search, searchable]);

  useEffect(() => {
    if (!open) return;

    if (searchable) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;

    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const isMulti = multi && Array.isArray(value);
  const safeValue = Array.isArray(value) ? value : [];

  const selectedLabel = isMulti
    ? safeValue
        .map((v) => normalized.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(", ")
    : (normalized.find((o) => o.value === value)?.label ?? "");
  const hasSelection = isMulti
    ? safeValue.length > 0
    : normalized.some((o) => o.value === value && String(value) !== "");

  const closeDropdown = () => {
    setOpen(false);
    setSearch("");
  };

  const toggleDropdown = () => {
    if (disabled) return;

    setOpen((p) => {
      const next = !p;
      if (!next) setSearch("");
      return next;
    });
  };

  return (
    <div
      ref={wrapRef}
      className={`${className} ${open ? openClassName : ""} ${
        disabled ? "is-disabled" : ""
      }`.trim()}
      onClick={(e) => {
        if (disabled) return;

        if (e.target.closest?.("[data-dropdown-menu]")) {
          return;
        }

        toggleDropdown();
      }}
      onKeyDown={(e) => {
        if (disabled) return;

        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleDropdown();
        }

        if (e.key === "Escape") {
          closeDropdown();
        }
      }}
      role="combobox"
      tabIndex={disabled ? -1 : 0}
      aria-controls={menuId}
      aria-disabled={disabled}
      aria-expanded={open}
      aria-haspopup="listbox"
    >
      {renderTrigger ? (
        renderTrigger({
          open,
          selectedLabel,
          hasSelection,
          placeholder,
          disabled,
        })
      ) : (
        <>
          {leftIcon && (
            <div className="filter-popup-container-rows-icon">{leftIcon}</div>
          )}

          <input
            className="filter-popup-container-rows-input-data"
            placeholder={translateText(placeholder)}
            value={selectedLabel}
            readOnly
            disabled={disabled}
          />

          {rightIcon && (
            <div className="filter-popup-container-rows-icon">{rightIcon}</div>
          )}
        </>
      )}

      {open && (
        <div
          id={menuId}
          className={menuClassName}
          data-dropdown-menu
          role="listbox"
          onClick={(e) => e.stopPropagation()}
        >
          {renderMenu ? (
            renderMenu({
              open,
              closeMenu: closeDropdown,
            })
          ) : (
            <>
              {searchable && (
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  placeholder={translateLabel("txt_web_vendor_search", "Search...")}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    all: "unset",
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "8px 10px",
                    marginBottom: "6px",
                  }}
                />
              )}

              {filtered.length
                ? filtered.map((opt, idx) => {
                    const isSelected = isMulti
                      ? safeValue.includes(opt.value)
                      : opt.value === value;

                    return (
                      <div
                        key={`${opt.value}-${idx}`}
                        className={`${optionClassName} ${
                          isSelected ? "selected" : ""
                        }`}
                        role="option"
                        aria-selected={isSelected}
                        onClick={(e) => {
                          e.stopPropagation();

                          if (isMulti) {
                            const newValue = safeValue.includes(opt.value)
                              ? safeValue.filter((v) => v !== opt.value)
                              : [...safeValue, opt.value];

                            onChange?.(newValue);
                          } else {
                            onChange?.(opt.value);
                            closeDropdown();
                          }
                        }}
                      >
                        {isMulti && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            style={{ margin: "7px" }}
                          />
                        )}

                        <span className="custom-dropdown-option-label">
                          {translateText(opt.label)}
                        </span>
                      </div>
                    );
                  })
                : searchable && (
                    <div style={{ padding: "8px 10px" }}>{translateLabel("txt_end_user_no_results", "No results")}</div>
                  )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
